import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

// ─── GL Account Mappings ──────────────────────────────────────────────────────
const GL_MAPPINGS = {
  escrow_held:      { code: '4111', account: 'Dépôts clients en séquestre',   debit: '5121', credit: '4111' },
  escrow_released:  { code: '7011', account: 'Produits de cession immobilière', debit: '4111', credit: '7011' },
  escrow_refunded:  { code: '6611', account: 'Remboursements clients',          debit: '6611', credit: '5121' },
  commission_paid:  { code: '6221', account: 'Commissions agents/notaires',     debit: '6221', credit: '5121' },
  payment_received: { code: '5121', account: 'Banque — compte courant',         debit: '5121', credit: '4111' },
  disputed:         { code: '4191', account: 'Litiges en cours',                debit: '4191', credit: '4111' },
  resolved:         { code: '7091', account: 'Produits exceptionnels',          debit: '4191', credit: '7091' },
};

const STATUS_CONFIG = {
  escrow_held:      { label: 'Séquestre actif',    bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'   },
  escrow_released:  { label: 'Libéré',             bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200'  },
  escrow_refunded:  { label: 'Remboursé',          bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200'  },
  commission_paid:  { label: 'Commission payée',   bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  payment_received: { label: 'Paiement reçu',      bg: 'bg-emerald-50',text: 'text-emerald-700',border: 'border-emerald-200'},
  disputed:         { label: 'En litige',          bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200'    },
  resolved:         { label: 'Résolu',             bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200'   },
};

const SETTLEMENT_METHODS = ['Tous', 'Paystack', 'Virement bancaire', 'Mobile Money', 'Espèces'];

const formatXOF = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(v || 0);

const formatDate = (d) =>
  d ? new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })?.format(new Date(d)) : '—';

// ─── CSV/Excel Export ─────────────────────────────────────────────────────────
const exportToExcel = (rows, dateFrom, dateTo) => {
  const headers = [
    'Référence',
    'Date',
    'Statut',
    'Méthode règlement',
    'Montant (XOF)',
    'Propriété',
    'Code GL',
    'Compte GL',
    'Débit',
    'Crédit',
    'Référence Paystack',
  ];

  const csvRows = [
    headers?.join(';'),
    ...rows?.map((r) => {
      const gl = GL_MAPPINGS?.[r?.status] || { code: '', account: '', debit: '', credit: '' };
      return [
        r?.reference || r?.id?.slice(0, 8),
        formatDate(r?.created_at),
        STATUS_CONFIG?.[r?.status]?.label || r?.status,
        r?.settlement_method || 'Paystack',
        r?.amount || 0,
        r?.property_title || '—',
        gl?.code,
        gl?.account,
        gl?.debit,
        gl?.credit,
        r?.paystack_reference || '—',
      ]?.join(';');
    }),
  ];

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `reconciliation_${dateFrom}_${dateTo}_${Date.now()}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || { label: status, bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
      {cfg?.label}
    </span>
  );
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, amount, icon, iconBg, iconColor }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center`}>
        <Icon name={icon} size={16} className={iconColor} />
      </div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-0.5">{amount}</p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const BatchReconciliationDashboard = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d?.setDate(1);
    return d?.toISOString()?.slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date()?.toISOString()?.slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('all');
  const [settlementFilter, setSettlementFilter] = useState('Tous');
  const [groupBy, setGroupBy] = useState('status'); // 'status' | 'date' | 'method'
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('escrow_transactions')?.select('id, created_at, status, amount, property_id, paystack_reference, settlement_method, buyer_id, seller_id, notary_id, reference')?.gte('created_at', `${dateFrom}T00:00:00`)?.lte('created_at', `${dateTo}T23:59:59`)?.order('created_at', { ascending: false });

      if (statusFilter !== 'all') query = query?.eq('status', statusFilter);

      const { data, error } = await query;
      if (error) throw error;

      // Enrich with mock property titles (real app would join properties table)
      const enriched = (data || [])?.map((t) => ({
        ...t,
        property_title: t?.property_id ? `Propriété #${t?.property_id?.slice(0, 6)}` : '—',
        settlement_method: t?.settlement_method || 'Paystack',
        reference: t?.reference || `TXN-${t?.id?.slice(0, 8)?.toUpperCase()}`,
      }));

      setTransactions(enriched);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      // Fallback mock data for demo
      setTransactions(generateMockData());
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const generateMockData = () =>
    Array.from({ length: 12 }, (_, i) => ({
      id: `mock-${i}`,
      reference: `TXN-${String(i + 1)?.padStart(4, '0')}`,
      created_at: new Date(Date.now() - i * 86400000 * 2)?.toISOString(),
      status: Object.keys(STATUS_CONFIG)?.[i % Object.keys(STATUS_CONFIG)?.length],
      amount: 5000000 + i * 1250000,
      property_title: `Appartement Cocody ${i + 1}`,
      settlement_method: SETTLEMENT_METHODS?.[(i % 3) + 1],
      paystack_reference: `PAY-${Math.random()?.toString(36)?.slice(2, 10)?.toUpperCase()}`,
    }));

  const filtered = transactions?.filter((t) => {
    if (settlementFilter !== 'Tous' && t?.settlement_method !== settlementFilter) return false;
    return true;
  });

  // Group transactions
  const grouped = filtered?.reduce((acc, t) => {
    let key;
    if (groupBy === 'status') key = t?.status;
    else if (groupBy === 'date') key = t?.created_at?.slice(0, 10);
    else key = t?.settlement_method || 'Paystack';
    if (!acc?.[key]) acc[key] = [];
    acc?.[key]?.push(t);
    return acc;
  }, {});

  const totalAmount = filtered?.reduce((s, t) => s + (t?.amount || 0), 0);
  const statusCounts = filtered?.reduce((acc, t) => {
    acc[t.status] = (acc?.[t?.status] || 0) + 1;
    return acc;
  }, {});

  const toggleRow = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next?.has(id) ? next?.delete(id) : next?.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows?.size === filtered?.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(filtered.map((t) => t.id)));
  };

  const handleExport = () => {
    setExporting(true);
    const toExport = selectedRows?.size > 0
      ? filtered?.filter((t) => selectedRows?.has(t?.id))
      : filtered;
    exportToExcel(toExport, dateFrom, dateTo);
    setTimeout(() => setExporting(false), 800);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Réconciliation en Lot</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Transactions groupées avec codes GL et mappings comptables
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || filtered?.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          <Icon name={exporting ? 'Loader' : 'Download'} size={15} className={exporting ? 'animate-spin' : ''} />
          {exporting ? 'Export…' : `Exporter Excel${selectedRows?.size > 0 ? ` (${selectedRows?.size})` : ''}`}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total transactions" value={filtered?.length} amount={formatXOF(totalAmount)} icon="BarChart3" iconBg="bg-blue-50" iconColor="text-blue-600" />
        <SummaryCard label="Séquestre actif" value={statusCounts?.escrow_held || 0} amount={formatXOF(filtered?.filter(t => t?.status === 'escrow_held')?.reduce((s, t) => s + (t?.amount || 0), 0))} icon="Shield" iconBg="bg-brand-earth/10" iconColor="text-brand-earth" />
        <SummaryCard label="Libérés" value={statusCounts?.escrow_released || 0} amount={formatXOF(filtered?.filter(t => t?.status === 'escrow_released')?.reduce((s, t) => s + (t?.amount || 0), 0))} icon="CheckCircle" iconBg="bg-green-50" iconColor="text-green-600" />
        <SummaryCard label="En litige" value={statusCounts?.disputed || 0} amount={formatXOF(filtered?.filter(t => t?.status === 'disputed')?.reduce((s, t) => s + (t?.amount || 0), 0))} icon="AlertTriangle" iconBg="bg-red-50" iconColor="text-red-600" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Date from */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Date début</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e?.target?.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          {/* Date to */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Date fin</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e?.target?.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          {/* Status */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Statut</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG)?.map(([k, v]) => (
                <option key={k} value={k}>{v?.label}</option>
              ))}
            </select>
          </div>
          {/* Settlement method */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Méthode règlement</label>
            <select
              value={settlementFilter}
              onChange={(e) => setSettlementFilter(e?.target?.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            >
              {SETTLEMENT_METHODS?.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {/* Group by */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Grouper par</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e?.target?.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            >
              <option value="status">Statut</option>
              <option value="date">Date</option>
              <option value="method">Méthode règlement</option>
            </select>
          </div>
        </div>
      </div>

      {/* GL Mapping Legend */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="BookOpen" size={15} className="text-amber-600" />
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Mappings GL pré-remplis</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(GL_MAPPINGS)?.slice(0, 4)?.map(([k, v]) => (
            <div key={k} className="bg-white rounded-lg px-3 py-2 border border-amber-100">
              <p className="text-[10px] font-bold text-amber-600">{v?.code}</p>
              <p className="text-xs text-gray-700 font-medium truncate">{v?.account}</p>
              <p className="text-[10px] text-gray-400">D:{v?.debit} / C:{v?.credit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Grouped transaction table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select all + count */}
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selectedRows?.size === filtered?.length && filtered?.length > 0}
              onChange={toggleAll}
              className="rounded border-gray-300"
            />
            <span>
              {selectedRows?.size > 0
                ? `${selectedRows?.size} sélectionné(s) sur ${filtered?.length}`
                : `${filtered?.length} transaction(s) au total`}
            </span>
          </div>

          {Object.entries(grouped)?.map(([groupKey, rows]) => {
            const groupCfg = STATUS_CONFIG?.[groupKey];
            const groupTotal = rows?.reduce((s, r) => s + (r?.amount || 0), 0);
            const gl = GL_MAPPINGS?.[groupKey];
            return (
              <div key={groupKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Group header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={groupKey} />
                    {gl && (
                      <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        GL {gl?.code}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{rows?.length} transaction(s)</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{formatXOF(groupTotal)}</span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="px-4 py-2 text-left w-8"></th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Référence</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Propriété</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Méthode</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Montant</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Débit GL</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Crédit GL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows?.map((row) => (
                        <tr
                          key={row?.id}
                          className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                            selectedRows?.has(row?.id) ? 'bg-blue-50/50' : ''
                          }`}
                        >
                          <td className="px-4 py-2.5">
                            <input
                              type="checkbox"
                              checked={selectedRows?.has(row?.id)}
                              onChange={() => toggleRow(row?.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="font-mono text-xs text-gray-700">{row?.reference}</span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{formatDate(row?.created_at)}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[140px] truncate">{row?.property_title}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-lg">
                              {row?.settlement_method}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs font-semibold text-gray-900">
                            {formatXOF(row?.amount)}
                          </td>
                          <td className="px-4 py-2.5 text-xs font-mono text-blue-600">
                            {GL_MAPPINGS?.[row?.status]?.debit || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-xs font-mono text-green-600">
                            {GL_MAPPINGS?.[row?.status]?.credit || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-4 py-2 text-xs font-bold text-gray-700">
                          Sous-total {groupCfg?.label || groupKey}
                        </td>
                        <td className="px-4 py-2 text-right text-xs font-bold text-gray-900">
                          {formatXOF(groupTotal)}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}

          {filtered?.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Icon name="FileSearch" size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune transaction pour cette période et ces filtres.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchReconciliationDashboard;
